<script setup lang="ts">
import DatePicker from 'primevue/datepicker';
import { computed, ref, useAttrs, useSlots, watch } from 'vue';
import DateTimePickerTimeControls from './DateTimePickerTimeControls.vue';
import {
  applyTimePartsToDate,
  readTimePartsFromDate,
  type DateTimePickerHourFormat,
  type DateTimePickerTimeParts,
} from '../utils/datetime-picker-time';

export type GpxDateTimeRangeValue = [Date | null, Date | null] | null;

/**
 * Date/time range picker: PrimeVue DatePicker (`selectionMode="range"`) with
 * manual start/end time in #buttonbar (built-in time spinners hidden).
 */
defineOptions({ inheritAttrs: false });

const model = defineModel<GpxDateTimeRangeValue>({ required: false });

const props = withDefaults(
  defineProps<{
    numberOfMonths?: number;
  }>(),
  {
    numberOfMonths: 2,
  },
);

const attrs = useAttrs();
const slots = useSlots();

const forwardedSlotNames = computed(() =>
  Object.keys(slots).filter((name) => name !== 'buttonbar'),
);

const GPX_DATETIME_PANEL_CLASS = 'gpx-datetime-picker-panel';

const pickerAttrs = computed(() => {
  const {
    class: _class,
    panelClass: _panelClass,
    'panel-class': _panelClassKebab,
    selectionMode: _selectionMode,
    numberOfMonths: _numberOfMonths,
    modelValue: _modelValue,
    'onUpdate:modelValue': _onUpdateModelValue,
    onShow: _onShow,
    onHide: _onHide,
    onClearClick: _onClearClick,
    ...rest
  } = attrs;

  return rest;
});

const panelClass = computed(() => [
  GPX_DATETIME_PANEL_CLASS,
  attrs.panelClass ?? attrs['panel-class'],
]);

const hourFormat = computed((): DateTimePickerHourFormat => {
  const raw = attrs.hourFormat ?? attrs['hour-format'];
  return raw === '24' ? '24' : '12';
});

const disabled = computed(() => Boolean(attrs.disabled));
const size = computed(
  () => attrs.size as 'small' | 'large' | undefined,
);

const rangeStart = computed(() => model.value?.[0] ?? null);
const rangeEnd = computed(() => model.value?.[1] ?? null);

const startHour = ref(12);
const startMinute = ref(0);
const startPm = ref(false);
const endHour = ref(12);
const endMinute = ref(0);
const endPm = ref(false);

let syncingFromModel = false;

function syncStartPartsFromDate() {
  const parts = readTimePartsFromDate(rangeStart.value, hourFormat.value);
  startHour.value = parts.hour;
  startMinute.value = parts.minute;
  startPm.value = parts.pm;
}

function syncEndPartsFromDate() {
  const parts = readTimePartsFromDate(rangeEnd.value, hourFormat.value);
  endHour.value = parts.hour;
  endMinute.value = parts.minute;
  endPm.value = parts.pm;
}

function syncAllPartsFromModel() {
  syncStartPartsFromDate();
  syncEndPartsFromDate();
}

function sameInstant(
  a: Date | null | undefined,
  b: Date | null | undefined,
): boolean {
  if (a == null && b == null) return true;
  if (a == null || b == null) return false;
  return a.getTime() === b.getTime();
}

function sameRange(
  a: GpxDateTimeRangeValue,
  b: GpxDateTimeRangeValue,
): boolean {
  if (a == null && b == null) return true;
  if (a == null || b == null) return false;
  return sameInstant(a[0], b[0]) && sameInstant(a[1], b[1]);
}

function mergeEndpoint(
  base: Date | null | undefined,
  parts: DateTimePickerTimeParts,
): Date | null {
  if (base == null || Number.isNaN(base.getTime())) {
    return null;
  }
  return applyTimePartsToDate(base, parts, hourFormat.value);
}

function publishRange() {
  const current = model.value;
  const nextStart = mergeEndpoint(current?.[0], {
    hour: startHour.value,
    minute: startMinute.value,
    pm: startPm.value,
  });
  const nextEnd = mergeEndpoint(current?.[1], {
    hour: endHour.value,
    minute: endMinute.value,
    pm: endPm.value,
  });

  if (nextStart == null && nextEnd == null) {
    if (model.value != null) {
      model.value = null;
    }
    return;
  }

  const next: [Date | null, Date | null] = [nextStart, nextEnd];
  if (!sameRange(next, model.value ?? null)) {
    model.value = next;
  }
}

watch(
  () => model.value,
  () => {
    syncingFromModel = true;
    syncAllPartsFromModel();
    syncingFromModel = false;
  },
  { immediate: true, deep: true },
);

watch(
  [startHour, startMinute, startPm],
  () => {
    if (syncingFromModel || rangeStart.value == null) {
      return;
    }
    publishRange();
  },
);

watch(
  [endHour, endMinute, endPm],
  () => {
    if (syncingFromModel || rangeEnd.value == null) {
      return;
    }
    publishRange();
  },
);

watch(hourFormat, () => {
  syncAllPartsFromModel();
});

function normalizeRangeValue(
  value: Date | Date[] | (Date | null)[] | null | undefined,
): GpxDateTimeRangeValue {
  if (value == null) {
    return null;
  }
  if (!Array.isArray(value)) {
    return [value, null];
  }
  const start = value[0] ?? null;
  const end = value.length > 1 ? (value[1] ?? null) : null;
  if (start == null && end == null) {
    return null;
  }
  return [start, end];
}

function onCalendarUpdate(
  value: Date | Date[] | (Date | null)[] | null | undefined,
) {
  syncingFromModel = true;
  const normalized = normalizeRangeValue(value);
  if (normalized == null) {
    model.value = null;
    syncingFromModel = false;
    return;
  }

  const [rawStart, rawEnd] = normalized;
  const mergedStart = mergeEndpoint(rawStart, {
    hour: startHour.value,
    minute: startMinute.value,
    pm: startPm.value,
  });
  const mergedEnd = mergeEndpoint(rawEnd, {
    hour: endHour.value,
    minute: endMinute.value,
    pm: endPm.value,
  });

  model.value = [mergedStart, mergedEnd];
  syncAllPartsFromModel();
  syncingFromModel = false;
}

function invokeAttrListener(key: 'onShow' | 'onHide' | 'onClearClick') {
  const handler = attrs[key];
  if (typeof handler === 'function') {
    handler();
  }
}

function onShow() {
  syncAllPartsFromModel();
  invokeAttrListener('onShow');
}

function onHide() {
  if (!syncingFromModel) {
    publishRange();
  }
  invokeAttrListener('onHide');
}

function onClearClick() {
  invokeAttrListener('onClearClick');
}

const startControlsDisabled = computed(
  () => disabled.value || rangeStart.value == null,
);
const endControlsDisabled = computed(
  () => disabled.value || rangeEnd.value == null,
);
</script>

<template>
  <DatePicker
    v-bind="pickerAttrs"
    :model-value="model"
    class="gpx-datetime-range-picker"
    :class="attrs.class"
    :panel-class="panelClass"
    selection-mode="range"
    :number-of-months="numberOfMonths"
    show-time
    show-button-bar
    @update:model-value="onCalendarUpdate"
    @show="onShow"
    @hide="onHide"
    @clear-click="onClearClick"
  >
    <template
      v-for="name in forwardedSlotNames"
      :key="name"
      #[name]="slotProps"
    >
      <slot :name="name" v-bind="slotProps ?? {}" />
    </template>

    <template #buttonbar>
      <div class="flex w-full items-center justify-center">
        <div
          class="flex min-w-0 flex-1 justify-center border-e border-surface-200 pe-6"
        >
          <DateTimePickerTimeControls
            v-model:hour="startHour"
            v-model:minute="startMinute"
            v-model:pm="startPm"
            :hour-format="hourFormat"
            :disabled="startControlsDisabled"
            :size="size"
          />
        </div>
        <div class="flex min-w-0 flex-1 justify-center ps-6">
          <DateTimePickerTimeControls
            v-model:hour="endHour"
            v-model:minute="endMinute"
            v-model:pm="endPm"
            :hour-format="hourFormat"
            :disabled="endControlsDisabled"
            :size="size"
          />
        </div>
      </div>
    </template>
  </DatePicker>
</template>
